<template>
  <article class="pc" @click="goToProgram" :class="{ 'pc--fav': isFav }">

    <!-- Image -->
    <div class="pc__img">
      <img :src="coverImage" :alt="item.programName || item.name" @error="onImgError" />

      <!-- Badge overlay -->
      <span v-if="topLabel" class="pc__badge" :class="badgeClass">{{ topLabel }}</span>

      <!-- Student choice badge -->
      <span v-if="item.is_student_choose" class="pc__badge pc__badge--choice">Выбор студентов</span>
    </div>

    <!-- Body -->
    <div class="pc__body">
      <!-- University -->
      <p class="pc__uni">{{ universityName }}</p>

      <!-- Program name -->
      <h3 class="pc__name">{{ item.programName || item.name }}</h3>

      <!-- Tag row -->
      <div class="pc__tags">
        <span v-if="categoryTitle" class="pc__tag">{{ categoryTitle }}</span>
        <span v-if="countryName" class="pc__tag pc__tag--blue">{{ countryName }}</span>
        <span v-if="degreeName" class="pc__tag pc__tag--gray">{{ degreeName }}</span>
      </div>

      <!-- Stats row -->
      <div class="pc__stats">
        <span v-if="item.gpa_min || item.gpa" class="pc__stat">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          GPA {{ item.gpa_min || item.gpa }}+
        </span>
        <span v-if="item.ielts_min || item.ielts" class="pc__stat">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M8 12l2 2 4-4"/></svg>
          IELTS {{ item.ielts_min || item.ielts }}+
        </span>
        <span v-if="deadline" class="pc__stat pc__stat--deadline">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          {{ deadline }}
        </span>
      </div>
    </div>

    <!-- Footer -->
    <div class="pc__footer" @click.stop>
      <button
        class="pc__fav-btn"
        :class="{ 'pc__fav-btn--active': isFav }"
        @click="toggleFav"
        :title="isFav ? 'Убрать из избранного' : 'В избранное'"
        v-if="$store.state.role === 'user'"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" :stroke="isFav ? '#e8192c' : 'currentColor'" stroke-width="2">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                :fill="isFav ? '#e8192c' : 'none'" />
        </svg>
      </button>

      <button class="pc__detail-btn" @click="goToProgram">
        Подробнее
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </button>
    </div>
  </article>
</template>

<script>
import axios from 'axios';
import { toast } from 'vue3-toastify';
import noPhoto from '@/assets/no-photo.png';

export default {
  name: 'ProgramCard',
  props: {
    item: { type: Object, required: true },
  },
  emits: ['fav-changed'],
  data() {
    return {
      isFav: Number(this.item?.is_favourite) === 1,
      imgFailed: false,
    };
  },
  computed: {
    coverImage() {
      if (this.imgFailed) return noPhoto;
      const imgs = this.item?.images;
      if (Array.isArray(imgs) && imgs.length) return imgs[0]?.image || imgs[0];
      return this.item?.image || noPhoto;
    },
    universityName() {
      const u = this.item?.university;
      return typeof u === 'string' ? u : u?.title || '';
    },
    categoryTitle() {
      return this.item?.subcategory?.category?.title
        || this.item?.category
        || '';
    },
    countryName() {
      return this.item?.country || this.item?.country_name || '';
    },
    degreeName() {
      return this.item?.studyDegree?.title || this.item?.study_degree_name || '';
    },
    topLabel() {
      return this.item?.top || '';
    },
    badgeClass() {
      const t = (this.topLabel || '').toLowerCase();
      if (t === 'elite') return 'pc__badge--elite';
      if (t === 'top') return 'pc__badge--top';
      return 'pc__badge--top';
    },
    deadline() {
      return this.item?.autumn_admission_end_date
        || this.item?.deadline
        || '';
    },
  },
  methods: {
    goToProgram() {
      this.$router.push({ name: 'program', params: { id: this.item.id } });
    },
    onImgError() {
      this.imgFailed = true;
    },
    async toggleFav() {
      const auth = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
      try {
        if (this.isFav) {
          await axios.delete(`/platform/client/users/favourites/${this.item.id}`, { headers: auth });
          this.isFav = false;
          toast.success('Убрано из избранного');
        } else {
          await axios.post('/platform/client/users/favourites',
            { university_program_id: this.item.id },
            { headers: auth }
          );
          this.isFav = true;
          toast.success('Добавлено в избранное');
        }
        this.$emit('fav-changed');
      } catch (e) {
        toast.error('Ошибка. Попробуйте снова.');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.pc {
  font-family: 'Manrope', sans-serif;
  -webkit-font-smoothing: antialiased;
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e8eaed;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: box-shadow 0.18s, transform 0.15s, border-color 0.18s;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.05);

  &:hover {
    box-shadow: 0 8px 24px rgba(16, 24, 40, 0.1);
    transform: translateY(-2px);
    border-color: rgba(0, 0, 0, 0.1);
  }
}

/* ── image ─────────────────────────────── */
.pc__img {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #f0f1f3;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

/* ── badges ─────────────────────────────── */
.pc__badge {
  position: absolute;
  top: 10px;
  left: 10px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 999px;

  &--elite {
    background: linear-gradient(135deg, #d4af37, #f5cc5b);
    color: #5a3e00;
  }
  &--top {
    background: rgba(232, 25, 44, 0.9);
    color: #fff;
  }
  &--choice {
    left: auto;
    right: 10px;
    background: rgba(15, 31, 61, 0.85);
    color: #fff;
  }
}

/* ── body ────────────────────────────────── */
.pc__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 18px 12px;
}

.pc__uni {
  margin: 0;
  font-size: 11.5px;
  font-weight: 600;
  color: #9ca3af;
  letter-spacing: 0.03em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pc__name {
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.01em;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ── tags ────────────────────────────────── */
.pc__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.pc__tag {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  font-weight: 600;
  color: #6b7280;
  background: #f3f4f6;
  border-radius: 6px;
  padding: 3px 9px;
  line-height: 1.4;

  &--blue  { color: #1d4ed8; background: #eff6ff; }
  &--gray  { color: #374151; background: #f3f4f6; }
}

/* ── stats ───────────────────────────────── */
.pc__stats {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 2px;
}

.pc__stat {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;

  svg { flex-shrink: 0; }

  &--deadline { color: #92400e; }
}

/* ── footer ──────────────────────────────── */
.pc__footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px 14px;
  border-top: 1px solid #f0f1f3;
  margin-top: auto;
}

.pc__fav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 9px;
  border: 1.5px solid #e5e7eb;
  background: #fff;
  cursor: pointer;
  color: #9ca3af;
  transition: border-color 0.12s, background 0.12s, color 0.12s;
  flex-shrink: 0;

  &:hover {
    border-color: #fca5a5;
    color: #e8192c;
    background: #fff1f2;
  }

  &--active {
    border-color: #e8192c;
    background: #fff1f2;
    color: #e8192c;
  }
}

.pc__detail-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: 9px;
  border: none;
  background: #0f1f3d;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: #1a3560; }
}
</style>
